import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import type { Database } from "../db/types.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type SikaGoalRow = Database["public"]["Tables"]["sika_goals"]["Row"];
export type SikaMessageRow = Database["public"]["Tables"]["sika_messages"]["Row"];

export interface SikaGoalWithMessages extends SikaGoalRow {
  messages: SikaMessageRow[];
}

export interface UseSikaGoalsState {
  goals: SikaGoalWithMessages[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSikaGoals(): UseSikaGoalsState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [goals, setGoals] = useState<SikaGoalWithMessages[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setGoals([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const { data: goalsData, error: goalsError } = await supabase
      .from("sika_goals")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (goalsError) {
      setGoals([]);
      setError(goalsError.message);
      setLoading(false);
      return;
    }

    const rows = goalsData ?? [];

    if (rows.length === 0) {
      setGoals([]);
      setLoading(false);
      return;
    }

    const goalIds = rows.map((g) => g.id);

    const { data: messagesData, error: messagesError } = await supabase
      .from("sika_messages")
      .select("*")
      .in("goal_id", goalIds)
      .order("created_at", { ascending: true });

    if (messagesError) {
      setGoals([]);
      setError(messagesError.message);
      setLoading(false);
      return;
    }

    const allMessages = messagesData ?? [];

    const messagesByGoal = new Map<string, SikaMessageRow[]>();
    for (const msg of allMessages) {
      if (msg.goal_id == null) continue;
      const arr = messagesByGoal.get(msg.goal_id);
      if (arr) {
        arr.push(msg);
      } else {
        messagesByGoal.set(msg.goal_id, [msg]);
      }
    }

    const combined: SikaGoalWithMessages[] = rows.map((g) => ({
      ...g,
      messages: messagesByGoal.get(g.id) ?? [],
    }));

    setGoals(combined);
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UseSikaGoalsState>(
    () => ({ goals, loading, error, refetch }),
    [goals, loading, error, refetch],
  );
}
